/**
 * printTemplatesAdmin.service.ts — Admin > Forms & Templates > Print Templates:
 * the account's print layouts (the legacy `quote_print_templates` table, NOT
 * the unrelated `print_templates` one) through the API v5 calls the corporate
 * dashboard's Print Templates page uses:
 *   - GET /print-templates?include_inactive=1, POST, PUT /{id};
 *   - DELETE /{id} (soft: active 0) and POST /{id}/restore;
 *   - GET /print-templates/disclosures (the account's active disclosures).
 *
 * A template has no HTML: it is ~80 show / hide switches grouped into 19
 * printable sections, each section's print position (`order*`), the discount
 * display mode, the disclosure to append and which documents offer it
 * (quotes, receipts, memberships). Labels and sections mirror the legacy admin
 * modal (admin-print-template-details.php), as corp's catalog does. The legacy
 * columns procLineItems, procEffectiveFeeTotal and orderGeneral are never
 * rendered or edited anywhere, so they are never sent. Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Catalog ───────────────────────────────────────────────────────────────────

/** Switches that sit outside the ordered sections. */
export const PRINT_HEADER_FIELDS = ['logoDisplay', 'forQuotes', 'forReceipts', 'forMemberships'] as const;

/** Every switch inside the ordered sections (section "Display" masters included). */
export const PRINT_SECTION_TOGGLES = [
  'patientName', 'apptDate', 'birthDateLine', 'apptLocation', 'plan', 'doctorName', 'quoteDate', 'coordName',
  'procHeader', 'procNames', 'procDescriptions', 'standardFees', 'effectiveFees', 'procSpecific',
  'procStandardFeeTotal', 'procDiscountTotal', 'procDiscounts', 'procPostDiscountTotal',
  'serviceHeader', 'anesthesiaFees', 'serviceNames', 'serviceFees', 'operatingRoomFees',
  'serviceFeeTotal', 'serviceDiscounts', 'servicePostDiscountTotal',
  'itemHeader', 'itemLineItems', 'itemDescriptions', 'itemSubFees', 'itemSalesTax', 'itemFees',
  'itemTotal', 'itemDiscounts', 'itemPostDiscountTotal',
  'membershipHeader', 'membershipName', 'membershipDescription', 'membershipFee',
  'membershipSubTotal', 'membershipDiscounts', 'membershipTotal',
  'packageHeader', 'packageName', 'packageDescription', 'packageFee',
  'packageSubTotal', 'packageDiscounts', 'packageTotal',
  'discountHeader', 'discountLineItems', 'discountAmounts', 'discountTotal',
  'depositLine', 'additionalPayments', 'balanceDue', 'grandTotal',
  'financingLineItems',
  'paymentType', 'paymentMethod', 'paymentRefNumber', 'paymentAmount', 'paymentNotes', 'paymentDate',
  'creditType', 'creditRefNumber', 'creditAmount', 'creditRedeemedAmount', 'creditBalance', 'creditNotes', 'creditDate',
  'disclosures',
  'signatureLine', 'witnessSignature',
] as const;

export const PRINT_BOOL_FIELDS = [...PRINT_HEADER_FIELDS, ...PRINT_SECTION_TOGGLES] as const;
export type PrintBoolField = (typeof PRINT_BOOL_FIELDS)[number];

/** One `order*` column per section: its 1-based print position. */
export const PRINT_ORDER_FIELDS = [
  'orderApptDetails', 'orderProceduresLine', 'orderProceduresTotals', 'orderServiceServicesLine',
  'orderServiceServicesTotals', 'orderMaterialsLine', 'orderMaterialsTotals', 'orderDiscountsLine',
  'orderDiscountsTotals', 'orderFinancing', 'orderTotal', 'orderDisclosures', 'orderSignature', 'orderPayment',
  'orderCredit', 'orderMembershipsLine', 'orderMembershipsTotals', 'orderPackagesLine', 'orderPackagesTotals',
] as const;
export type PrintOrderField = (typeof PRINT_ORDER_FIELDS)[number];

export type PrintSectionField =
  | { kind: 'toggle'; key: PrintBoolField; label: string }
  /** discountDisplayType: 0 = all discounts, 1 = only those applied to the total. */
  | { kind: 'discountDisplay'; label: string }
  /** disclosureType: the account's disclosure to append. */
  | { kind: 'disclosure'; label: string };

export interface PrintSection {
  orderKey: PrintOrderField;
  title: string;
  /** The section's "Display" switch. Only Financing, Disclosures and Patient Signature have one. */
  displayKey?: PrintBoolField;
  /** Beside the title, e.g. "receipts only". */
  note?: string;
  fields: PrintSectionField[];
  /** The legacy position of a new template. */
  defaultOrder: number;
}

const t = (key: PrintBoolField, label: string): PrintSectionField => ({ kind: 'toggle', key, label });

export const PRINT_SECTIONS: readonly PrintSection[] = [
  {
    orderKey: 'orderApptDetails', title: 'General Patient & Appointment Info', defaultOrder: 1,
    fields: [
      t('patientName', 'Patient Name'), t('apptDate', 'Procedure Date'), t('birthDateLine', 'Birth Date'),
      t('apptLocation', 'Location'), t('plan', 'Plan ID'), t('doctorName', 'Doctor'),
      t('quoteDate', 'Quote Date'), t('coordName', 'Coordinator'),
    ],
  },
  {
    orderKey: 'orderProceduresLine', title: 'Procedures - Itemized', defaultOrder: 2,
    fields: [
      t('procHeader', 'Heading'), t('procNames', 'Sub-Category Name'), t('procDescriptions', 'Description'),
      t('standardFees', 'Standard Fee'), t('effectiveFees', 'Effective Fee'), t('procSpecific', 'Specific Name'),
    ],
  },
  {
    orderKey: 'orderProceduresTotals', title: 'Procedures - Totals', defaultOrder: 3,
    fields: [
      t('procStandardFeeTotal', 'Pre-discount Total'), t('procDiscountTotal', 'Standard Fee Discount'),
      t('procDiscounts', 'Applied Discounts'), t('procPostDiscountTotal', 'Post-discount Total'),
    ],
  },
  {
    orderKey: 'orderServiceServicesLine', title: 'Surgery Services - Itemized', defaultOrder: 4,
    fields: [
      t('serviceHeader', 'Heading'), t('anesthesiaFees', 'Anesthesia'), t('serviceNames', 'Service Name'),
      t('serviceFees', 'Effective Fee'), t('operatingRoomFees', 'Operating Room'),
    ],
  },
  {
    orderKey: 'orderServiceServicesTotals', title: 'Surgery Services - Totals', defaultOrder: 5,
    fields: [
      t('serviceFeeTotal', 'Pre-discount Total'), t('serviceDiscounts', 'Applied Discounts'),
      t('servicePostDiscountTotal', 'Post-discount Total'),
    ],
  },
  {
    orderKey: 'orderMaterialsLine', title: 'Products - Itemized', defaultOrder: 6,
    fields: [
      t('itemHeader', 'Heading'), t('itemLineItems', 'Product Name'), t('itemDescriptions', 'Description'),
      t('itemSubFees', 'Pre-Tax Fee'), t('itemSalesTax', 'Sales Tax'), t('itemFees', 'Post-Tax Fee'),
    ],
  },
  {
    orderKey: 'orderMaterialsTotals', title: 'Products - Totals', defaultOrder: 7,
    fields: [
      t('itemTotal', 'Pre-discount Total'), t('itemDiscounts', 'Applied Discounts'),
      t('itemPostDiscountTotal', 'Post-discount Total'),
    ],
  },
  {
    orderKey: 'orderDiscountsLine', title: 'Applied Discounts - Itemized', defaultOrder: 8,
    fields: [
      t('discountHeader', 'Heading'),
      { kind: 'discountDisplay', label: 'Show' },
      t('discountLineItems', 'Discount Name'), t('discountAmounts', 'Amount'),
    ],
  },
  {
    orderKey: 'orderDiscountsTotals', title: 'Applied Discounts - Totals', defaultOrder: 9,
    fields: [t('discountTotal', 'Total')],
  },
  { orderKey: 'orderFinancing', title: 'Financing Options', defaultOrder: 10, displayKey: 'financingLineItems', fields: [] },
  {
    orderKey: 'orderTotal', title: 'Quote Totals', defaultOrder: 11,
    fields: [
      t('depositLine', 'Deposit'), t('additionalPayments', 'Additional Payments'),
      t('balanceDue', 'Balance Due'), t('grandTotal', 'Grand Total'),
    ],
  },
  {
    orderKey: 'orderDisclosures', title: 'Disclosures', defaultOrder: 12, displayKey: 'disclosures',
    fields: [{ kind: 'disclosure', label: 'Disclosure Type' }],
  },
  {
    orderKey: 'orderSignature', title: 'Patient Signature', defaultOrder: 13, displayKey: 'signatureLine',
    fields: [t('witnessSignature', 'Witness Signature')],
  },
  {
    orderKey: 'orderPayment', title: 'Payment Info', note: 'receipts only', defaultOrder: 14,
    fields: [
      t('paymentType', 'Type'), t('paymentMethod', 'Method'), t('paymentRefNumber', 'Reference Number'),
      t('paymentAmount', 'Amount'), t('paymentNotes', 'Notes'), t('paymentDate', 'Date'),
    ],
  },
  {
    orderKey: 'orderCredit', title: 'Payment Credit Info', note: 'receipts only', defaultOrder: 15,
    fields: [
      t('creditType', 'Type'), t('creditRefNumber', 'Reference Number'), t('creditAmount', 'Credit Value'),
      t('creditRedeemedAmount', 'Redeemed Amount'), t('creditBalance', 'Remaining Balance'),
      t('creditNotes', 'Credit Notes'), t('creditDate', 'Purchase Date'),
    ],
  },
  {
    orderKey: 'orderMembershipsLine', title: 'Memberships - Itemized', defaultOrder: 16,
    fields: [
      t('membershipHeader', 'Heading'), t('membershipName', 'Membership Name'),
      t('membershipDescription', 'Description'), t('membershipFee', 'Fee'),
    ],
  },
  {
    orderKey: 'orderMembershipsTotals', title: 'Memberships - Totals', defaultOrder: 17,
    fields: [
      t('membershipSubTotal', 'Pre-discount Total'), t('membershipDiscounts', 'Applied Discounts'),
      t('membershipTotal', 'Post-discount Total'),
    ],
  },
  {
    orderKey: 'orderPackagesLine', title: 'Packages - Itemized', defaultOrder: 18,
    fields: [
      t('packageHeader', 'Heading'), t('packageName', 'Package Name'),
      t('packageDescription', 'Description'), t('packageFee', 'Fee'),
    ],
  },
  {
    orderKey: 'orderPackagesTotals', title: 'Packages - Totals', defaultOrder: 19,
    fields: [
      t('packageSubTotal', 'Pre-discount Total'), t('packageDiscounts', 'Applied Discounts'),
      t('packageTotal', 'Post-discount Total'),
    ],
  },
];

export const PRINT_SECTION_BY_KEY = Object.fromEntries(PRINT_SECTIONS.map((s) => [s.orderKey, s])) as Record<
  PrintOrderField,
  PrintSection
>;

export const PRINT_HEADER_LABELS: Record<(typeof PRINT_HEADER_FIELDS)[number], string> = {
  logoDisplay: 'Display Logo',
  forQuotes: 'Quotes',
  forReceipts: 'Receipts',
  forMemberships: 'Memberships',
};

export const DISCOUNT_DISPLAY_OPTIONS = [
  { value: 0, label: 'All discounts' },
  { value: 1, label: 'Only discounts applied to total quote' },
];

/** Every form key a section owns: its Display switch, its switches, and its selects. */
export function sectionFormKeys(section: PrintSection): (keyof PrintTemplateForm)[] {
  const keys: (keyof PrintTemplateForm)[] = [];
  if (section.displayKey) keys.push(section.displayKey);
  for (const f of section.fields) {
    if (f.kind === 'toggle') keys.push(f.key);
    else if (f.kind === 'discountDisplay') keys.push('discountDisplayType');
    else keys.push('disclosureType');
  }
  return keys;
}

// ─── Records ───────────────────────────────────────────────────────────────────

export type PrintFlags = Record<PrintBoolField, boolean>;
export type PrintOrder = Record<PrintOrderField, number>;

export interface PrintTemplate {
  quotePrintTemplateID: number;
  name: string;
  /** False = deleted. */
  active: boolean;
  /** disclosures.disclosuresID in this account; 0 = none. */
  disclosureType: number;
  /** The disclosure's name (also for an inactive one); null when none. */
  disclosureName: string | null;
  discountDisplayType: number;
  flags: PrintFlags;
  order: PrintOrder;
}

export interface PrintDisclosure {
  disclosuresID: number;
  name: string;
}

const isOn = (v: unknown) => v === true || v === 1 || v === '1';

export function toPrintTemplate(raw: Record<string, unknown>): PrintTemplate {
  const flags = {} as PrintFlags;
  for (const key of PRINT_BOOL_FIELDS) flags[key] = isOn(raw[key]);
  const order = {} as PrintOrder;
  for (const key of PRINT_ORDER_FIELDS) order[key] = Number(raw[key] ?? 0) || 0;
  const disclosureName = raw.disclosureName == null ? '' : String(raw.disclosureName);
  return {
    quotePrintTemplateID: Number(raw.quotePrintTemplateID ?? raw.id ?? 0),
    name: String(raw.name ?? ''),
    active: raw.active !== false && raw.active !== 0 && raw.active !== '0',
    disclosureType: Number(raw.disclosureType ?? 0) || 0,
    disclosureName: disclosureName === '' ? null : disclosureName,
    discountDisplayType: Number(raw.discountDisplayType ?? 0) || 0,
    flags,
    order,
  };
}

// ─── Section order ─────────────────────────────────────────────────────────────

/**
 * Sections in print order. A section at position 0 (added after the template
 * was last saved) goes after every positioned one, in legacy order — where the
 * legacy renderer prints it.
 */
export function sectionSequence(order: PrintOrder): PrintOrderField[] {
  return [...PRINT_SECTIONS]
    .sort((a, b) => {
      const pa = order[a.orderKey] || Number.MAX_SAFE_INTEGER;
      const pb = order[b.orderKey] || Number.MAX_SAFE_INTEGER;
      return pa !== pb ? pa - pb : a.defaultOrder - b.defaultOrder;
    })
    .map((s) => s.orderKey);
}

/** 1-based positions, as the legacy save handler wrote them. */
export function orderFromSequence(sequence: PrintOrderField[]): PrintOrder {
  const order = {} as PrintOrder;
  sequence.forEach((key, i) => {
    order[key] = i + 1;
  });
  let next = sequence.length + 1;
  for (const key of PRINT_ORDER_FIELDS) if (!(key in order)) order[key] = next++;
  return order;
}

// ─── The form ──────────────────────────────────────────────────────────────────

/**
 * The builder's state: one key per switch, so each one gets its own "modified"
 * pill and revert (useTrackedForm compares key by key).
 */
export type PrintTemplateForm = {
  name: string;
  disclosureType: number;
  discountDisplayType: number;
  sequence: PrintOrderField[];
} & PrintFlags;

export function formFromTemplate(tpl: PrintTemplate): PrintTemplateForm {
  return {
    name: tpl.name,
    disclosureType: tpl.disclosureType,
    discountDisplayType: tpl.discountDisplayType,
    sequence: sectionSequence(tpl.order),
    ...tpl.flags,
  };
}

/**
 * A new template, as corp seeds it: everything shown except the receipt-only
 * Payment / Credit blocks and the witness line; discounts limited to those on
 * the total (the legacy default); offered on quotes; sections in legacy order.
 */
export function newPrintTemplateForm(): PrintTemplateForm {
  const flags = {} as PrintFlags;
  for (const key of PRINT_BOOL_FIELDS) flags[key] = !(key.startsWith('payment') || key.startsWith('credit'));
  flags.witnessSignature = false;
  flags.forReceipts = false;
  flags.forMemberships = false;
  return {
    name: '',
    disclosureType: 0,
    discountDisplayType: 1,
    sequence: [...PRINT_SECTIONS].sort((a, b) => a.defaultOrder - b.defaultOrder).map((s) => s.orderKey),
    ...flags,
  };
}

export function flagsOf(form: PrintTemplateForm): PrintFlags {
  const flags = {} as PrintFlags;
  for (const key of PRINT_BOOL_FIELDS) flags[key] = form[key];
  return flags;
}

/** The full write shape POST and PUT take (corp sends it whole on both). */
export function printTemplatePayload(form: PrintTemplateForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    disclosureType: form.disclosureType,
    discountDisplayType: form.discountDisplayType,
    ...flagsOf(form),
    ...orderFromSequence(form.sequence),
  };
}

/** Which documents offer it, e.g. "Quotes, Receipts"; "—" for none. */
export function offeredForLabel(flags: Pick<PrintFlags, 'forQuotes' | 'forReceipts' | 'forMemberships'>): string {
  const parts: string[] = [];
  if (flags.forQuotes) parts.push('Quotes');
  if (flags.forReceipts) parts.push('Receipts');
  if (flags.forMemberships) parts.push('Memberships');
  return parts.length ? parts.join(', ') : '—';
}

export type PrintDocType = 'quote' | 'receipt' | 'membership';

export const PRINT_DOC_LABEL: Record<PrintDocType, string> = {
  quote: 'Quote',
  receipt: 'Receipt',
  membership: 'Membership Agreement',
};

/** The documents a template is offered for, in corp's order (Quote when none). */
export function offeredDocTypes(flags: Pick<PrintFlags, 'forQuotes' | 'forReceipts' | 'forMemberships'>): PrintDocType[] {
  const list: PrintDocType[] = [];
  if (flags.forQuotes) list.push('quote');
  if (flags.forReceipts) list.push('receipt');
  if (flags.forMemberships) list.push('membership');
  return list.length ? list : ['quote'];
}

/**
 * Corp's rules: an error keeps Save off; a suggestion only warns. Name
 * uniqueness is a suggestion (legacy never enforced it) among the account's
 * other live templates.
 */
export function printTemplateChecks(
  form: PrintTemplateForm,
  templates: PrintTemplate[],
  selfId: number | null,
): { errors: string[]; suggestions: string[] } {
  const errors: string[] = [];
  const suggestions: string[] = [];
  const name = form.name.trim();
  if (!name) errors.push('Template name is required');
  if (
    name &&
    templates.some((x) => x.active && x.quotePrintTemplateID !== selfId && x.name.trim().toLowerCase() === name.toLowerCase())
  ) {
    suggestions.push(`"${name}" is already used by another template`);
  }
  if (!form.forQuotes && !form.forReceipts && !form.forMemberships) {
    suggestions.push('Not offered for quotes, receipts or memberships — nobody will be able to pick this template');
  }
  if (form.disclosures && !form.disclosureType) suggestions.push('Disclosures section is displayed but no disclosure is selected');
  if (form.forReceipts && PRINT_BOOL_FIELDS.filter((k) => k.startsWith('payment')).every((k) => !form[k])) {
    suggestions.push('Offered for receipts but no Payment Info fields are shown');
  }
  return { errors, suggestions };
}

// ─── API ───────────────────────────────────────────────────────────────────────

function rows(data: unknown): Record<string, unknown>[] {
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

/** Every template, deleted ones too (Show deleted needs no refetch). */
export function listPrintTemplates(): Promise<PrintTemplate[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/print-templates', { params: { include_inactive: 1 } })
    .then((r) => rows(r.data?.data).map(toPrintTemplate));
}

export function listPrintDisclosures(): Promise<PrintDisclosure[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/print-templates/disclosures').then((r) =>
    rows(r.data?.data).map((d) => ({ disclosuresID: Number(d.disclosuresID ?? 0), name: String(d.name ?? '') })),
  );
}

export function createPrintTemplate(form: PrintTemplateForm): Promise<PrintTemplate> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/print-templates', printTemplatePayload(form))
    .then((r) => toPrintTemplate(r.data?.data ?? {}));
}

export function updatePrintTemplate(id: number, form: PrintTemplateForm): Promise<PrintTemplate> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/print-templates/${id}`, printTemplatePayload(form))
    .then((r) => toPrintTemplate(r.data?.data ?? {}));
}

export function deletePrintTemplate(id: number): Promise<void> {
  return apiClient.delete(`/print-templates/${id}`).then(() => undefined);
}

export function restorePrintTemplate(id: number): Promise<void> {
  return apiClient.post(`/print-templates/${id}/restore`).then(() => undefined);
}
